<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";
import { WarnTriangleFilled } from '@element-plus/icons-vue';

// Services
import VehicleServiceRequest from '@/api/services/vehicleRequest.service';

// Icons Components
import IconVehicle from '@/components/icons/IconVehicle.vue';

// My Components
import FuelReceiptTable from '@/components/shared/Vehicles/FuelReceiptTable.vue';

// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'finish']);

// eslint-disable-next-line no-undef
const props = defineProps({
    vehicleRequest: Object,
});

const uiStore = useUiStore();

const request = computed(() => props.vehicleRequest ?? null)

const vehicle = computed(() => request.value?.vehicle ?? null)

const employee = computed(() => request.value?.employee ?? null)

const fuelTypeOptions = [
    { label: 'Diesel', value: 'diesel' },
    { label: 'Gasolina', value: 'gasoline' },
    { label: 'Eléctrico', value: 'electric' },
    { label: 'Híbrido', value: 'hybrid' },
    { label: 'Híbrido enchufable', value: 'plugin_hybrid' },
]

const getFuelType = computed(() => {
    if (!vehicle.value?.fuelType) {
        return '';
    }

    return fuelTypeOptions.find(option => option.value === vehicle.value.fuelType)?.label ?? vehicle.value.fuelType;
})

const yesNo = (value) => {
    if (value === true) return 'Sí';
    if (value === false) return 'No';
    return '';
}

const closeDialog = () => {
    emit('close');
}

const isSuccessfulResponse = (res) => {
    return res && (res.status === 200 || res.status === 201);
}

const buildSaveParams = () => {
    const params = {
        ...request.value,
    }

    params.vehicle = request.value.vehicle?.['@id'] ?? request.value.vehicle;

    if (Array.isArray(request.value.fuelReceiptMediaObjects)) {
        params.fuelReceiptMediaObjects = request.value.fuelReceiptMediaObjects
            .map(ticket => ticket?.['@id'] ?? ticket)
            .filter(Boolean);
    }

    return params;
}

const saveCurrentRequest = () => {
    return VehicleServiceRequest.correctVehicleRequest(request.value.id, buildSaveParams());
}

const confirmRequest = async () => {
    if (!request.value?.id || request.value?.status !== 'returned') {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido la solicitud a confirmar.',
            duration: 6000
        })
        return;
    }

    try {
        uiStore.startLoading();
        const saveRes = await saveCurrentRequest();
        if (!isSuccessfulResponse(saveRes)) {
            notification.addNotification({
                type: 'error',
                message: 'No se ha podido guardar la solicitud. No se confirma la recepción.',
                duration: 6000
            })
            return;
        }

        const res = await VehicleServiceRequest.confirmVehicleRequest(request.value.id)
        if (isSuccessfulResponse(res)) {
            notification.addNotification({
                type: 'success',
                message: 'Solicitud confirmada',
                duration: 4000
            })
            emit('finish')
            emit('close')
        }
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'No se ha podido guardar la solicitud. No se confirma la recepción.',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}
</script>

<template>
    <div class="vehicle-confirm-dialog">
        <div class="my-header">
            <div class="title-text ml-4">
                <IconVehicle class="iconVehicle" />
                Confirmar recepción
            </div>
            <img class="close-icon pointer justify-content-end" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
                @click="closeDialog()">
        </div>
        <div class="row d-flex dialog-content" v-if="request">
            <div class="col-12 row d-flex mb-3">
                <div class="col-md-6 el-col-xs-24">
                    <label class="w-100">Coche:</label>
                    <el-input :model-value="vehicle?.displayName" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24">
                    <label class="w-100">Matrícula:</label>
                    <el-input :model-value="vehicle?.licensePlate" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Marca:</label>
                    <el-input :model-value="vehicle?.brand" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Modelo:</label>
                    <el-input :model-value="vehicle?.model" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Empleado:</label>
                    <el-input :model-value="employee?.fullName" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Tipo de combustible:</label>
                    <el-input :model-value="getFuelType" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Fecha y hora inicio:</label>
                    <el-date-picker :model-value="request.startDate" type="datetime" format="DD/MM/YYYY HH:mm"
                        class="mb-1 w-100" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Fecha y hora fin:</label>
                    <el-date-picker :model-value="request.endDate" type="datetime" format="DD/MM/YYYY HH:mm"
                        class="mb-1 w-100" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="request.closedAt">
                    <label class="w-100">Fecha y hora devolución:</label>
                    <el-date-picker :model-value="request.closedAt" type="datetime" format="DD/MM/YYYY HH:mm"
                        class="mb-1 w-100" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="request.startKm">
                    <label class="w-100">Kilómetros de inicio:</label>
                    <el-input :model-value="request.startKm" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="request.endKm">
                    <label class="w-100">Kilómetros finales:</label>
                    <el-input :model-value="request.endKm" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="request.traveledKm">
                    <label class="w-100">Kilómetros recorridos:</label>
                    <el-input :model-value="request.traveledKm" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="request.fullTank !== undefined">
                    <label class="w-100">Depósito lleno:</label>
                    <el-input :model-value="yesNo(request.fullTank)" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="request.adBlueAdded !== undefined">
                    <label class="w-100">AdBlue añadido:</label>
                    <el-input :model-value="yesNo(request.adBlueAdded)" disabled />
                </div>
                <div class="col-md-12 el-col-xs-24 mt-2" v-if="request.purpose">
                    <label class="w-100">Motivo:</label>
                    <textarea :value="request.purpose" class="w-100 p-2" rows="2" disabled></textarea>
                </div>
                <div class="col-md-12 el-col-xs-24 mt-2" v-if="request.observations">
                    <label class="w-100">Observaciones:</label>
                    <textarea :value="request.observations" class="w-100 p-2" rows="2" disabled></textarea>
                </div>
                <div class="col-md-12 el-col-xs-24 mt-2" v-if="request.incidents">
                    <label class="w-100">Incidentes:</label>
                    <textarea :value="request.incidents" class="w-100 p-2" rows="3" disabled></textarea>
                </div>
            </div>
            <div class="col-12 row d-flex mb-3">
                <FuelReceiptTable :request="request" :isReturning="false" :canEdit="false" />
            </div>
            <div class="col-12 vehicle-confirm-warning">
                <div class="vehicle-confirm-warning__header">
                    <el-icon class="vehicle-confirm-warning__icon">
                        <WarnTriangleFilled />
                    </el-icon>
                    <span>Acción requerida:</span>
                </div>
                <p class="vehicle-confirm-warning__text">
                    Por favor, verifica que hayas recibido el vehículo y las llaves, revisa los justificantes adjuntos
                    y confirma la recepción.
                </p>
                <button class="btn btn_green px-4 vehicle-confirm-warning__button" @click="confirmRequest()">
                    ✓ Confirmar recepción de vehículo y llaves
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
:deep(.el-input.is-disabled .el-input__wrapper),
textarea:disabled {
    background-color: var(--el-fill-color-light);
}

.my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.iconVehicle {
    width: 40px;
    fill: $bg-blue;
}

:deep(.el-input.is-disabled .el-input__inner),
textarea:disabled {
    cursor: default;
    color: rgb(94, 94, 94) !important;
    -webkit-text-fill-color: rgb(94, 94, 94);

}

:deep(.el-input.is-disabled .el-input__icon) {
    cursor: default;
}

.dialog-content {
    margin: 0;
}

.vehicle-confirm-warning {
    padding: 18px 20px;
    margin-top: 8px;
    border: 1px solid #faad14;
    border-radius: 6px;
    background-color: #fff3cd;
}

.vehicle-confirm-warning__header {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4f5968;
    font-size: 20px;
    font-weight: 800;
}

.vehicle-confirm-warning__icon {
    color: #faad14;
    font-size: 22px;
}

.vehicle-confirm-warning__text {
    margin: 18px 0 22px;
    color: #4f5968;
    font-size: 16px;
    line-height: 1.45;
}

.vehicle-confirm-warning__button {
    min-height: 40px;
    font-weight: 800;
}

@media (max-width: 767px) {
    .vehicle-confirm-dialog {
        padding: 0 2px 8px;
    }

    .my-header {
        align-items: flex-start;
        gap: 12px;
    }

    .my-header .title-text {
        margin-left: 0 !important;
        font-size: 26px;
        line-height: 1.15;
    }

    .vehicle-confirm-warning__button {
        width: 100%;
        white-space: normal;
    }
}
</style>
